import React from "react";
import { ShieldCheck, Youtube, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { navLinks } from "../mock";
import { useTheme } from "../theme";
import { KickLogo, DiscordLogo } from './SocialIcons';
import { REWARDS_DISCORD_URL } from '../content/rewards';
import { SOCIAL_URLS } from '../content/socials';
import './footerSocials.css';

const socials = [
  { label: "YouTube", icon: Youtube, href: SOCIAL_URLS.youtube },
  { label: "Kick", icon: KickLogo, href: process.env.REACT_APP_KICK_CHANNEL_URL },
  { label: "Twitter", icon: Twitter, href: SOCIAL_URLS.twitter },
  { label: "Discord", icon: DiscordLogo, href: REWARDS_DISCORD_URL },
];

const Footer = () => {
  const { theme } = useTheme();
  const logo = theme !== "venom" ? "/gv-logo-onyx.png" : "/gv-logo.png";

  return (
    <footer data-testid="site-footer" className="relative mt-28 border-t border-emerald-500/15 bg-[#0a0b0e]/70">
      {/* subtle accent glow line */}
      <div className="footer-brand-line pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      <div className="footer-brand-glow pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-40 w-[36rem] max-w-full rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <img src={logo} alt="GVRewards" data-testid="footer-brand-logo" className="h-9 w-auto" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              Compete in high-prized monthly leaderboards, unlock VIP rewards and
              claim the best benefits on Stake — powered by GreenVenom.
            </p>
            <div className="mt-6 flex items-center gap-2.5">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  data-social-brand={s.label.toLowerCase()}
                  className="footer-social-link"
                >
                  <s.icon data-testid={`footer-icon-${s.label.toLowerCase()}`} className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigate */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300/80 mb-4">
              Navigate
            </h4>
            <ul className="space-y-2.5">
              {navLinks.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    <span className="h-1 w-1 rounded-full bg-emerald-400/0 transition-colors group-hover:bg-emerald-400" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Responsible play + CTA */}
          <div className="md:col-span-4">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300/80 mb-4">
              Responsible Play
            </h4>
            <div className="flex items-start gap-3 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-white/10">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <p className="text-xs leading-relaxed text-slate-400">
                You must be at least 18 years old and agree to our Terms &
                Conditions. Please gamble responsibly.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-6 sm:flex-row">
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} GVRewards. All rights reserved.
          </p>
          <p className="text-xs text-slate-600">
            Not affiliated with Stake.com. 18+ only · Gamble responsibly.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
